import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  minAge: number;
  eventTitle: string;
  /** Called only once the visitor confirms they meet the age requirement. */
  onConfirmed: () => void;
}

export function calculateAge(dob: string) {
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

/**
 * Age check shown before a shared ticket link opens. Nothing is stored — the
 * organiser still verifies ID at the door and checkout re-checks the profile.
 */
const AgeGateDialog = ({ open, onOpenChange, minAge, eventTitle, onConfirmed }: Props) => {
  const [dob, setDob] = useState("");
  const age = useMemo(() => (dob ? calculateAge(dob) : null), [dob]);
  const tooYoung = age !== null && age < minAge;
  const future = age !== null && age < 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setDob(""); }}>
      <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-400" />This event is {minAge}+
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            “{eventTitle}” is age restricted. Only people aged {minAge} and over may buy or use a ticket, and the venue
            checks ID at the door — an underage ticket will be refused entry with no refund.
          </p>
          <div>
            <Label htmlFor="age-gate-dob">Your date of birth</Label>
            <Input
              id="age-gate-dob"
              type="date"
              value={dob}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDob(e.target.value)}
              className="bg-black border-gray-800 mt-1"
            />
          </div>
          {age !== null && !future && (
            <p className={`text-xs ${tooYoung ? "text-red-400" : "text-emerald-400"}`}>
              {tooYoung
                ? `You are ${age} — this event is restricted to ${minAge} and over.`
                : `You are ${age} — you meet the ${minAge}+ requirement.`}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={age === null || tooYoung || future}
            onClick={() => { onConfirmed(); onOpenChange(false); setDob(""); }}
          >
            Continue to tickets
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgeGateDialog;
